import { NetzTranslateService } from './../../services/netz-translate.service';
import { Router } from '@angular/router';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { SectionIdentifier } from '../../+shared/components/review-section/review-section.domain';
import { ComponentCanDeactivate } from '../../+shared/guards/pending-changes-guard.servie';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { addRemoveAnimation } from 'src/app/+shared/animations/addRemove.animation';
import { mapDrrCcdDeviceApplicationDtoToForm, mapFormToDrrCcdDeviceApplicationDto } from './technical-data.mappers';
import { DrrCcdDeviceService } from './technical-data.service';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ToastNotificationService } from 'src/app/+shared/services/toast-notification.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { ApplicationStepType } from 'src/app/+shared/components/application-steps-generic/application-steps-generic.models';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { DrrCcdDataStore } from '../drr-ccd-data.store';
import { UserService } from 'src/app/services/user.service';
import { DrrCcdCommissioningData, DrrCcdDeviceApplicationDto } from './technical-data.domain';
import { DrrCcdTypes } from './technical-data.domain';

@Component({
  selector: 'app-technical-data',
  templateUrl: './technical-data.component.html',
  styleUrls: ['./technical-data.component.scss'],
  animations: [addRemoveAnimation],
})
export class TechnicalDataComponent extends DestroyableComponent implements OnInit, ComponentCanDeactivate {
  SectionIdentifier = SectionIdentifier;

  form: FormGroup;
  translations = {};
  subType: string;
  loading: boolean = false;

  // for template only usage
  applictionData: DrrCcdDeviceApplicationDto;

  ApplicationStepType = ApplicationStepType;
  forceAllowDeactivate: boolean;
  drrCcdCommissioningData: DrrCcdCommissioningData;
  commissioningDataForm: FormGroup;

  @Input() applicationId: number;
  @Input() isDetailsView: boolean;

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private router: Router,
    private drrCcdDeviceService: DrrCcdDeviceService,
    private toaster: ToastNotificationService,
    private errorService: ErrorService,
    private translationService: NetzTranslateService,
    private localStorage: LocalStorageService,
    private store: DrrCcdDataStore,
    private userService: UserService
  ) {
    super();
    this.applicationId = this.applicationId || parseInt(this.localStorage.getItem('application_id') || '0');
    this.form = fb.group({
      applicationId: fb.control(null),
      type: fb.control(this.subType, [Validators.required]),
      devices: fb.array([]),
      simultaneouslyRequiredPower: fb.control(null, [Validators.required]),
      totalHeatPumpPower: fb.control({ value: 0, disabled: true }),
      totalAirConditionerPower: fb.control({ value: 0, disabled: true }),
      totalWallboxPower: fb.control({ value: 0, disabled: true }),
      totalStoragePower: fb.control({ value: 0, disabled: true }),
    });

    this.preventLeak(this.form.statusChanges).subscribe(_ => this.cd.detectChanges());
    this.preventLeak(this.form.controls.devices.valueChanges).subscribe(_ => {
      this.calculateTotalPower(this.form.controls.devices as FormArray);
    });
  }

  canDeactivate: () => boolean | Promise<boolean> = () =>
    !this.form.dirty || !this.commissioningDataForm.dirty || this.forceAllowDeactivate;

  ngOnInit(): void {
    if (this.applicationId) {
      this.getExistingApplication(this.applicationId);
    } else {
      this.addControllableConsumptionDevice(null); // add empty device by default
    }
  }

  get devices(): FormGroup[] {
    return (this.form.controls.devices as FormArray).controls as FormGroup[];
  }

  get isApplicationOwner(): boolean {
    return this.userService.userId.toString() === this.applictionData?.user_id?.toString();
  }

  get drrCcdDeviceLabelKey(): string {
    return this.applictionData?.is_registration_device
      ? 'drr-ccd-devices.add-drr-device-btn'
      : 'drr-ccd-devices.add-controllable-consumption-device-btn';
  }

  initCcdDrrDeviceForm(ccdDrrDeviceForm: FormGroup, parentForm: FormGroup) {
    parentForm.addControl('device', ccdDrrDeviceForm);

    this.cd.detectChanges();
  }

  addControllableConsumptionDevice(rawValue?: any) {
    const devices = this.form.controls.devices as FormArray;
    devices.push(
      this.fb.group({
        rawValue: this.fb.control(rawValue),
      })
    );
    this.calculateTotalPower(devices);

    this.cd.detectChanges();
  }

  removeDevice(index: number) {
    (this.form.controls.devices as FormArray).removeAt(index);
  }

  save(options?: { preventGoToNextPage: boolean }): Promise<void> {
    return new Promise((resolve, reject) => {
      mapFormToDrrCcdDeviceApplicationDto({ ...this.form.getRawValue(), ...this.commissioningDataForm.getRawValue() }).then(
        requestModel => {
          this.loading = true;
          if (this.form.get('applicationId').value) {
            this.preventLeak(this.preventLeak(this.drrCcdDeviceService.updateDrrCcdDeviceApplication(requestModel))).subscribe(
              response => {
                this.loading = false;
                this.errorService.throwErrorIfFailure(response, true);
                this.toaster.success(this.translationService.translateString('submitted.new_changes'));
                if (!options?.preventGoToNextPage) {
                  this.navigateToNextPage();
                }
                resolve();
              },
              err => {
                this.loading = false;
                this.toaster.error(this.translationService.translateString('submitted.error'));
                reject(err);
              }
            );
          } else {
            this.preventLeak(this.drrCcdDeviceService.createDrrCcdDeviceApplication(requestModel)).subscribe(
              response => {
                this.loading = false;
                this.errorService.throwErrorIfFailure(response, true);
                this.toaster.success(this.translationService.translateString('submitted.new_changes'));
                this.form.get('applicationId').setValue(response);
                this.navigateToNextPage();
                resolve();
              },
              err => {
                this.loading = false;
                this.toaster.error(this.translationService.translateString('submitted.error'));
                reject(err);
              }
            );
          }
        }
      );
    });
  }

  public initCommissioningDataForm(commissioningDataForm: FormGroup) {
    this.commissioningDataForm = commissioningDataForm;
  }

  private calculateTotalPower(devices: FormArray): void {
    const powerTotals = devices.controls.reduce(
      (totals, device) => {
        const deviceValue = device.get('device')?.value;
        const consumption = Number(deviceValue?.totalConsumption) || 0;

        switch (deviceValue?.ccdType) {
          case DrrCcdTypes.heatPump:
            totals.totalHeatPumpPower += consumption;
            break;
          case DrrCcdTypes.airConditioner:
            totals.totalAirConditionerPower += consumption;
            break;
          case DrrCcdTypes.wallBox:
            totals.totalWallboxPower += consumption;
            break;
          case DrrCcdTypes.storage:
            totals.totalStoragePower += consumption;
            break;
        }
        return totals;
      },
      {
        totalHeatPumpPower: 0,
        totalAirConditionerPower: 0,
        totalWallboxPower: 0,
        totalStoragePower: 0,
      }
    );

    this.form.patchValue({
      totalHeatPumpPower: powerTotals.totalHeatPumpPower,
      totalAirConditionerPower: powerTotals.totalAirConditionerPower,
      totalWallboxPower: powerTotals.totalWallboxPower,
      totalStoragePower: powerTotals.totalStoragePower,
    });
  }

  private navigateToNextPage() {
    this.forceAllowDeactivate = true;
    this.router.navigate(['drr-ccd-devices/personal-data']).finally(() => (this.forceAllowDeactivate = false));
  }

  private getExistingApplication(applicationId: number) {
    this.loading = true;
    this.preventLeak(this.store.getApplicationDto()).subscribe(
      response => {
        this.loading = false;
        if (!response) return;
        this.applictionData = response;
        var formValue = mapDrrCcdDeviceApplicationDtoToForm(response);
        this.form.patchValue(formValue);
        for (const device of formValue.devices) {
          this.addControllableConsumptionDevice(device.device);
        }
        this.drrCcdCommissioningData = {
          commissioning_date: formValue.commissioningDate,
          is_commissioning_done: formValue.isCommissioningDone,
          is_commissioning_confirmed: formValue.isCommissioningConfirmed,
          commissioning_document: formValue.commissioningDocument,
          commissioning_document_id: formValue.commissioningDocumentId,
        };

        if (!formValue.devices?.length) {
          this.addControllableConsumptionDevice();
        }
      },
      error => (this.loading = false)
    );
  }
}
